import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { MarketingAnalyticsApiService } from './marketing-analytics-api.service';
import {
  generateEventId,
  generateSessionId,
  getDeviceType,
  validateEvent,
} from '@naybrs/analytics';
import type {
  AnalyticsEvent,
  EventType,
  EventData,
  ContextInfo,
  DeviceType,
} from '@naybrs/analytics';

/**
 * Marketing analytics service for about.naybrs.com.
 * Tracks page views, section navigation, form submissions (seller join),
 * CTA clicks, scroll depth, and errors.
 */
@Injectable({ providedIn: 'root' })
export class MarketingAnalyticsService {
  private readonly buffer: AnalyticsEvent[] = [];
  private readonly maxBufferSize = 10;
  private readonly flushIntervalMs = 30000;
  private sessionId: string = generateSessionId();

  constructor(
    private readonly apiService: MarketingAnalyticsApiService,
    private readonly router: Router,
    private readonly zone: NgZone,
  ) {
    this.scheduleFlush();
    this.registerUnloadFlush();
    this.trackPageViews();
  }

  // ====================================================================
  // Public tracking methods
  // ====================================================================

  /** Track a page view (auto-registered via router) */
  async trackPageView(page: string, pageTitle?: string): Promise<void> {
    await this.pushEvent('page_view', {
      type: 'page_view',
      page,
      pageTitle,
    });
  }

  /** Track section navigation click (scroll-to-section) */
  async trackSectionClick(sectionId: string): Promise<void> {
    await this.pushEvent('click', {
      type: 'click',
      element: `section_nav_${sectionId}`,
    });
  }

  /** Track seller join form submission */
  async trackJoinFormSubmit(step: string, fieldCount: number, completionTime?: number): Promise<void> {
    await this.pushEvent('form_submit', {
      type: 'form_submit',
      formName: `seller_join_${step}`,
      fieldCount,
      completionTime,
    });
  }

  /** Track CTA button click */
  async trackCtaClick(ctaName: string): Promise<void> {
    await this.pushEvent('click', {
      type: 'click',
      element: `cta_${ctaName}`,
    });
  }

  /** Track scroll depth on marketing pages */
  async trackScrollDepth(depthPercent: number, direction: 'up' | 'down'): Promise<void> {
    await this.pushEvent('scroll', {
      type: 'scroll',
      scrollDepthPercent: depthPercent,
      scrollSpeed: 0,
      direction,
    });
  }

  /** Track marketing site error */
  async trackError(errorMessage: string, errorCode?: string, context?: string): Promise<void> {
    await this.pushEvent('error', {
      type: 'error',
      errorMessage,
      errorCode,
      stackTrace: context,
    });
  }

  // ====================================================================
  // Core buffer + flush
  // ====================================================================

  private async pushEvent(eventType: EventType, eventData: EventData, contextInfo?: ContextInfo): Promise<void> {
    const event: AnalyticsEvent = {
      eventId: generateEventId(),
      userId: 'anonymous',
      eventType,
      timestamp: Date.now(),
      sessionId: this.sessionId,
      userType: 'guest',
      deviceType: this.getDeviceType(),
      eventData,
      contextInfo: {
        url: window.location.href,
        referrer: document.referrer,
        ...contextInfo,
      },
      createdAt: new Date().toISOString(),
    };

    const validation = validateEvent(event);
    if (!validation.isValid) {
      console.warn(`[MarketingAnalytics] Validation failed for ${eventType}: ${validation.reason ?? 'unknown'}`);
      return;
    }

    this.buffer.push(event);
    if (this.buffer.length >= this.maxBufferSize) {
      await this.flushBuffer();
    }
  }

  private async flushBuffer(): Promise<void> {
    if (this.buffer.length === 0) return;
    const events = [...this.buffer];
    this.buffer.length = 0;

    try {
      await firstValueFrom(this.apiService.batchFlush(events));
    } catch (err: unknown) {
      const error: Error = err instanceof Error ? err : new Error(String(err));
      console.warn(`[MarketingAnalytics] Flush failed: ${error.message}`);
    }
  }

  private flushBufferSync(): void {
    if (this.buffer.length === 0) return;
    const success = this.apiService.sendBeacon(this.buffer);
    if (success) {
      this.buffer.length = 0;
    }
  }

  private scheduleFlush(): void {
    this.zone.runOutsideAngular(() => {
      setInterval(() => {
        if (document.hidden) return;
        void this.flushBuffer();
      }, this.flushIntervalMs);
    });
  }

  private registerUnloadFlush(): void {
    window.addEventListener('beforeunload', () => this.flushBufferSync());
  }

  // ====================================================================
  // Auto page-view tracking
  // ====================================================================

  private trackPageViews(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        void this.trackPageView(event.urlAfterRedirects);
      });
  }

  // ====================================================================
  // Helpers
  // ====================================================================

  private getDeviceType(): DeviceType {
    return getDeviceType(navigator.userAgent);
  }
}
